import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  IconButton, 
  Tooltip, 
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Slide,
  alpha
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  BarChart as StatisticsIcon,
  FindInPage as RecordsIcon,
  Add as SubmitIcon,
  Close as CloseIcon,
  Gavel as MethodIcon,
  ContactMail as ContactIcon,
  Map as MapIcon
} from '@mui/icons-material';
import ComprehensiveDashboard from '../common/ComprehensiveDashboard';

const Header = () => {
  const [showDashboard, setShowDashboard] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  // Handle scroll effect for transparent to solid header
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navigationItems = [
    { label: 'Home', path: '/', icon: <HomeIcon /> },
    { label: 'About', path: '/about', icon: <InfoIcon /> },
    { label: 'Judicial Agency Map', shortLabel: 'Agency Map', path: '/map', icon: <MapIcon /> },
    { label: 'Request a Questionnaire', shortLabel: 'Questionnaire', path: '/submit-case', icon: <SubmitIcon /> },
    { label: 'Submit Misconduct', shortLabel: 'Submit', path: '/submit-source', icon: <SubmitIcon /> },
    { label: 'Method', path: '/method', icon: <MethodIcon /> },
    { label: 'Contact', path: '/contact', icon: <ContactIcon /> }
  ];

  const isActive = (path) => location.pathname === path;

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={toggleMobileMenu}        PaperProps={{
        sx: { 
          width: '280px',
          backgroundImage: 'linear-gradient(135deg, #FFFFFF 0%, #FFF8E1 100%)',
          color: '#1A1A1A',
          borderRight: `1px solid ${theme.palette.divider}`
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="h6">Menu</Typography>
        <IconButton onClick={toggleMobileMenu} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navigationItems.map((item) => (
          <ListItem
            button
            key={item.path}
            component={Link}
            to={item.path}
            onClick={toggleMobileMenu}
            sx={{
              py: 2,
              backgroundColor: isActive(item.path) ? 'rgba(230, 184, 0, 0.15)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(230, 184, 0, 0.1)'
              },
              borderRadius: '0 8px 8px 0',
              marginRight: 2,
              marginLeft: 1,
              transition: 'all 0.2s ease'
            }}
          >
            <ListItemIcon sx={{ color: isActive(item.path) ? theme.palette.accent.light : 'inherit', minWidth: '40px' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label} 
              primaryTypographyProps={{ 
                fontWeight: isActive(item.path) ? 700 : 500,
                color: isActive(item.path) ? theme.palette.accent.light : 'inherit'
              }}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={scrolled ? 4 : 0}
        sx={{
          background: scrolled 
            ? 'linear-gradient(90deg, #FFFFFF 0%, #FFF8E1 100%)' 
            : 'linear-gradient(90deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 248, 225, 0.98) 100%)',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          borderBottom: scrolled ? 'none' : `1px solid ${theme.palette.custom.richGold}30`,
          boxShadow: scrolled ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ py: scrolled ? 0.5 : 1, flexWrap: 'nowrap', minHeight: 64, transition: 'all 0.3s ease' }}>
          <Container maxWidth="xl" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', minWidth: 0 }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                <img 
                  src="/ReckoningProject.logo.png" 
                  alt="The Reckoning Project" 
                  style={{ 
                    height: scrolled ? '40px' : '48px',
                    width: 'auto',
                    transition: 'all 0.3s ease',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                  }} 
                />
              </Box>
              <Typography 
                variant="h5" 
                component="div"
                sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(90deg, #1A1A1A 30%, #D4AF37 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0px 2px 5px rgba(0,0,0,0.1)',
                  letterSpacing: '-0.01em',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
              </Typography>
            </Link>

            <Box sx={{ flexGrow: 1 }} />

            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.25 }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: '#1A1A1A',
                      fontSize: '0.8125rem',
                      px: 1.25,
                      py: 0.75,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      borderRadius: '6px',
                      position: 'relative',
                      overflow: 'hidden',
                      fontWeight: isActive(item.path) ? 600 : 500,
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: isActive(item.path) ? 'translateX(-50%)' : 'translateX(-50%) scaleX(0)',
                        height: '2px',
                        width: '60%',
                        background: theme.palette.custom.richGold,
                        borderRadius: '2px 2px 0 0',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(230, 184, 0, 0.1)',
                        '&:before': {
                          transform: 'translateX(-50%) scaleX(1)',
                        }
                      }
                    }}
                  >
                    {item.shortLabel || item.label}
                  </Button>
                ))}
              </Box>
            )}

            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={toggleMobileMenu}
                sx={{ 
                  ml: 1,
                  color: '#1A1A1A',
                  backgroundColor: 'rgba(230, 184, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(230, 184, 0, 0.2)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Container>
        </Toolbar>
      </AppBar>

      {renderMobileMenu()}

      <ComprehensiveDashboard
        open={showDashboard}
        onClose={() => setShowDashboard(false)}
      />
    </>
  );
};

export default Header;